import { Hub } from "aws-amplify/utils";
import { getCurrentUser } from "aws-amplify/auth";
import { Store } from "redux";
import { amplifyLogger } from "./AmplifyLogger";
import { setInitialPagePath, signIn } from "./store/userSession/userSessionActions";
import { CustomOAuthState } from "./types/CustomOAuthState";
import { LocalStorageUtils } from "./utils/LocalStorageUtils";
import { setInternalUserPoolSession } from "./utils/amplifyConfigUtils";
import { UserGroup } from "./gen/clients/llts";
import { USER_SESSION_OVERRIDE_SESSION_STORAGE_KEY } from "./utils/constants";
import { AuthUtils, IdTokenClaims } from "./utils/AuthUtils";

const COUPA_USERS_GROUP_NAME = "CoupaUser";
const INTERNAL_USER_IDP_NAME = "LLS-IDP";

interface IdentityClaim {
  providerName?: string;
}

export class AuthEventListener {
  public static async init(store: Store): Promise<void> {
    Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          amplifyLogger.info("user signed in");
          // The v6 payload does not carry tokens, so the claims have to be fetched separately.
          this.dispatchSignIn(store, payload.data.username);
          // Clear session timestamp when a new session starts.
          LocalStorageUtils.storeSessionTimestamp(null);
          break;
        case "signedOut":
          window.sessionStorage.removeItem(USER_SESSION_OVERRIDE_SESSION_STORAGE_KEY);
          setInternalUserPoolSession(false);
          amplifyLogger.info("user signed out");
          break;
        case "signInWithRedirect_failure":
          amplifyLogger.error("user sign in failed");
          break;
        case "tokenRefresh":
          amplifyLogger.info("token refresh succeeded");
          break;
        case "tokenRefresh_failure":
          amplifyLogger.error("token refresh failed");
          break;
        case "customOAuthState": {
          const stateData = payload.data;
          if (stateData) {
            const customOAuthState: CustomOAuthState = JSON.parse(stateData);
            if (customOAuthState.initialPagePath) {
              store.dispatch(setInitialPagePath(customOAuthState.initialPagePath));
            }
          }
          break;
        }
        default: {
          // do nothing
        }
      }
    });

    try {
      const user = await getCurrentUser();
      if (user) {
        await this.dispatchSignIn(store, user.username);
      }
    } catch (e) {
      amplifyLogger.info("User is not authenticated.", e);
    }
  }

  private static async dispatchSignIn(store: Store, username: string): Promise<void> {
    const claims = await AuthUtils.getIdTokenClaims();
    const userGroups = this.getUserGroups(claims);
    const email = this.getEmail(claims);
    const isFederatedSsoAccount = this.isFederatedSsoAccount(claims, userGroups);
    const firstName = this.getFirstName(claims);
    const lastName = this.getLastName(claims);
    const externalGroups = this.getExternalGroups(claims);
    store.dispatch(
      signIn(username, email || "", userGroups, isFederatedSsoAccount, firstName, lastName, externalGroups)
    );
  }

  private static getIdentities(claims?: IdTokenClaims): IdentityClaim[] {
    return (claims?.identities as IdentityClaim[] | undefined) || [];
  }

  private static getUserGroups(claims?: IdTokenClaims): (UserGroup | string)[] {
    if (claims) {
      // If the user is authenticated using a 3-rd party identity provider, then assume it is a client employee.
      const cognitoGroups = (claims["cognito:groups"] as (UserGroup | string)[] | undefined) || [];
      const identities = this.getIdentities(claims);
      if (identities.length > 0 && identities[0].providerName !== INTERNAL_USER_IDP_NAME) {
        const isClientAdmin = cognitoGroups.includes(UserGroup.CLIENT);
        return isClientAdmin ? [UserGroup.CLIENT] : [UserGroup.CLIENT_EMPLOYEE];
      }
      return cognitoGroups;
    }
    return [];
  }

  private static getExternalGroups(claims?: IdTokenClaims): string[] | undefined {
    const externalGroupsStr = claims?.["custom:externalGroups"] as string | undefined;
    if (externalGroupsStr) {
      return externalGroupsStr
        .replace(/^\[|]$/g, "")
        .split(",")
        .map(group => group.trim());
    }
    return undefined;
  }

  private static getEmail(claims?: IdTokenClaims): string | undefined {
    const email = claims?.email as string | undefined;
    if (!email) {
      amplifyLogger.error("Email is not provided.");
    }
    return email;
  }

  private static isFederatedSsoAccount(claims?: IdTokenClaims, userGroups: (UserGroup | string)[] = []): boolean {
    return this.getIdentities(claims).length > 0 || userGroups.includes(COUPA_USERS_GROUP_NAME);
  }

  private static getFirstName(claims?: IdTokenClaims): string | undefined {
    const firstName = claims?.given_name as string | undefined;
    if (!firstName) {
      window.console.log("given_name not found");
    }
    return firstName;
  }

  private static getLastName(claims?: IdTokenClaims): string | undefined {
    const lastName = claims?.family_name as string | undefined;
    if (!lastName) {
      window.console.log("family_name not found");
    }
    return lastName;
  }
}

export default AuthEventListener;
